// Some short-hand helpers for doing some async/await stuff more gracefully

class TimeoutError extends Error {

}

class AsyncTools {
   static get TimeoutError() { return TimeoutError }

   /***
    * Async version of setTimeout, to sleep/pause for the provided milliseconds
    * @param ms - Milliseconds to sleep/pause for
    * @returns {Promise<any>}
    */
   static sleep(ms=0) { return new Promise(r => setTimeout(r, ms)) }

   /***
    * Async wrapper around a task to time how long the task takes. Returns the time as an integer number of milliseconds
    */
   static async time(task) {
      let start = new Date()
      let out;
      try {
         out = await task()
      } catch (err) {
         // Save elapsed time for error handling
         err.taskElapsed = new Date() - start
         throw(err)
      }
      return [out, new Date() - start]
   }

   /***
    * Simplified way of running an array of Tasks in Parallel, with support for Limiting
    * the number of tasks (defaults to 4 if not specified).
    * @param tasks - Array of async functions to call & run in parallel
    * @param limit - Maximum Tasks to perform at once
    */
   static async parallelLimit(tasks, limit=4) {
      // Track running Tasks
      let running = new Set()
      let result = []

      for (let i=0; i<tasks.length; i++) {
         let task = tasks[i]
         // Wait for a task to finish if we're at our concurrency limit
         while (running.size >= limit)
            await Promise.race(running)

         // Extend the Task to remove itself from the running set once complete
         const promise = (async () => {
            result[i] = await task()
            running.delete(promise)
         })()

         // Add promise to set of running tasks
         running.add(promise)
      }

      // Wait for last tasks to finish
      if (running.size > 0)
         await Promise.all(running)
      return result
   }

   /***
    * Variation on parallelLimit taking an Array of records, and preserving the Task results.
    * @param recs - Array of records to execute the task on
    * @param task - Async function to call for each record, passed the record, and the array index
    * @param limit - Maximum Tasks to perform at once
    * @returns {Promise<array>} - Array of results from each function
    */
   static async mapLimit(recs, task, limit=4) {
      // Track running Tasks
      let running = new Set()
      let result = []

      for (let i=0; i<recs.length; i++) {
         // Wait for a task to finish if we're at our concurrency limit
         while (running.size >= limit)
            await Promise.race(running)

         // Create task to run on the record, get result, and remove itself from the running set once complete
         const promise = (async () => {
            result[i] = await task(recs[i], i)

            running.delete(promise)
         })()

         // Add promise to set of running tasks
         running.add(promise)
      }

      // Wait for last tasks to finish
      if (running.size > 0)
         await Promise.all(running)
      return result
   }

   /***
    * Calls parallelLimit to iterate one task over an array of records. Syntactically closer to mapLimit, but doesn't keep the results
    * @param recs - Array of records to execute the task on
    * @param task - Async function to call for each record, passed the record, and the array index
    * @param limit - Maximum Tasks to perform at once
    */
   static async eachLimit(recs, task, limit=4) {
      await AsyncTools.parallelLimit(recs.map((rec, i) => async () => { return await task(rec, i) }), limit)
   }

   static async eachSeries(recs, task) {
      let results = []
      for (let i=0; i<recs.length; i++) {
         results.push(await task(recs[i], i, recs))
      }
      return results
   }

   static async doWithTimeout(task, ms) {
      let th
      return await Promise.race([
         new Promise((resolve, reject) => {
            task().then((r) => {
               resolve(r)
               if (th) clearTimeout(th)
            })
            .catch((e) => {
               reject(e)
               if (th) clearTimeout(th)
            })
         }),
         new Promise((resolve, reject) => {
            th = setTimeout(() => reject(new TimeoutError('Task Timeout')), ms)
         })
      ])
   }

   /***
    * Return a debounced async function call
    */
   static debounceAsync(func, timeout = 300) {
      let timer;
      return async (...args) => {
         clearTimeout(timer)
         timer = setTimeout(async () => { await func.apply(this, args); }, timeout)
      };
   }

}

module.exports = AsyncTools
